import React, { useState, useEffect } from 'react';
import { X, ChevronRight, ChevronDown, Folder, FileText, Search, Loader2 } from 'lucide-react';
import axios from 'axios';

const TreeNode = ({ node, onSelect, level = 0 }) => {
  const [isOpen, setIsOpen] = useState(false);
  const hasChildren = node.children && node.children.length > 0;

  return (
    <div className="select-none">
      <div 
        className={`flex items-center py-1.5 px-2 hover:bg-blue-50 cursor-pointer group rounded-sm transition-colors ${level > 0 ? 'ml-4 border-l border-gray-200' : ''}`}
        onClick={() => hasChildren ? setIsOpen(!isOpen) : onSelect(node)}
      >
        <div className="flex items-center gap-2 flex-1">
          {hasChildren ? (
            isOpen ? <ChevronDown size={14} className="text-blue-600"/> : <ChevronRight size={14} className="text-gray-400"/>
          ) : (
            <div className="w-[14px]" /> 
          )}
          
          {hasChildren ? (
            <Folder size={16} className={`${isOpen ? 'text-yellow-500' : 'text-yellow-400'} fill-current`} />
          ) : (
            <FileText size={16} className="text-blue-400" />
          )}

          <span className={`text-[12px] ${!hasChildren ? 'font-bold text-blue-900' : 'text-gray-700'}`}>
            {node.classstructureid} <span className="text-gray-400 mx-1">|</span> {node.description}
          </span>
        </div>

        {!hasChildren && (
          <button 
            onClick={(e) => { e.stopPropagation(); onSelect(node); }}
            className="opacity-0 group-hover:opacity-100 bg-[#1a3353] text-white text-[10px] px-3 py-0.5 rounded shadow-sm transition-all"
          >
            Select
          </button>
        )}
      </div>
      
      {isOpen && hasChildren && (
        <div className="animate-in slide-in-from-top-1 duration-200">
          {node.children.map(child => (
            <TreeNode key={child.classstructureid} node={child} onSelect={onSelect} level={level + 1} />
          ))}
        </div>
      )}
    </div>
  );
};

const ClassificationTree = ({ isOpen, onClose, onSelect }) => {
  const [treeData, setTreeData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      axios.get('/api/classifications/tree')
        .then(res => {
          setTreeData(res.data);
          setLoading(false);
        })
        .catch(() => setLoading(false));
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[4000] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white w-[550px] shadow-2xl border border-gray-300 flex flex-col max-h-[85vh] rounded-sm overflow-hidden animate-in zoom-in-95 duration-200">
        
        {/* Header */}
        <div className="bg-[#1a3353] text-white px-4 py-3 flex justify-between items-center shadow-md">
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold uppercase tracking-wider">Classification Hierarchy</span>
          </div>
          <button onClick={onClose} className="hover:bg-red-600 p-1 rounded-full transition-colors">
            <X size={18}/>
          </button>
        </div>

        {/* Search */}
        <div className="p-3 bg-gray-50 border-b">
          <div className="relative">
            <input 
              className="w-full border border-gray-300 p-2 pl-9 text-xs outline-none focus:border-blue-600 rounded"
              placeholder="Search classification..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Search className="absolute left-3 top-2.5 text-gray-400" size={16} />
          </div>
        </div>

        {/* Tree Content */}
        <div className="overflow-y-auto flex-1 p-4 bg-white min-h-[400px] custom-scrollbar">
          {loading ? (
            <div className="flex flex-col items-center justify-center h-full text-blue-800 gap-2">
              <Loader2 className="animate-spin" size={32}/>
              <span className="text-xs font-bold">Building Hierarchy...</span>
            </div>
          ) : (
            <div className="space-y-1">
              {treeData.map(rootNode => (
                <TreeNode key={rootNode.classstructureid} node={rootNode} onSelect={onSelect} />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 p-3 border-t flex justify-end">
          <button onClick={onClose} className="bg-white border px-6 py-1.5 text-[11px] font-bold text-gray-600 hover:bg-gray-100 rounded shadow-sm">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClassificationTree;